import { useEffect } from 'react'
import './App.css'
import Logo from './Logo.png'

const whatsappNumber = '919950732346'

const images = {
  lounge: 'https://pplx-res.cloudinary.com/image/upload/pplx_search_images/49ce456f15ef73215e3effe408078bb592971d97.jpg',
  simulator: 'https://pplx-res.cloudinary.com/image/upload/pplx_search_images/37d1c4ea06bc273e767ab86e6e2cc68d8a55be24.jpg',
  accent: 'https://pplx-res.cloudinary.com/image/upload/pplx_search_images/55cada8cf73dc7eff79e90144caac74d69f487ed.jpg',
}

const experiences = [
  {
    title: 'PlayStation 5',
    description: 'Premium console sessions for solo gaming, duos, and high-energy friend groups.',
    meta: '₹200 for one person · ₹70 extra per additional player',
  },
  {
    title: 'Xbox',
    description: 'Comfortable, social, and perfect for casual competition and evening hangouts.',
    meta: '₹200 for one person · ₹70 extra per additional player',
  },
  {
    title: 'Wheel Simulator',
    description: 'A more immersive racing experience designed for shorter but high-impact sessions.',
    meta: '₹200 for 30 minutes per person',
  },
  {
    title: 'PS5 VR',
    description: 'Step into a sharper, more intense experience with virtual reality gaming.',
    meta: '₹200 for 30 minutes per person',
  },
]

const faqs = [
  {
    q: 'How does booking work?',
    a: 'Fill the form and tap the booking button. Your details open instantly in WhatsApp as a prefilled message.',
  },
  {
    q: 'Do party bookings need advance payment?',
    a: 'No. Party enquiries can be sent directly on WhatsApp without advance payment.',
  },
  {
    q: 'What are your opening hours?',
    a: 'House of Games is open daily from 11:00 AM to 8:30 PM.',
  },
  {
    q: 'Do you have snacks?',
    a: 'Yes. Snacks are available during sessions and group bookings.',
  },
]

function buildWhatsAppLink(formData) {
  const lines = [
    'New Booking Enquiry - House of Games',
    `Name: ${formData.name || '-'}`,
    `Phone: ${formData.phone || '-'}`,
    `Booking Type: ${formData.bookingType || '-'}`,
    `Date: ${formData.date || '-'}`,
    `Time: ${formData.time || '-'}`,
    `People: ${formData.people || '-'}`,
    `Message: ${formData.message || '-'}`,
    'Location: Chawri Market, Bhiwadi, near Genesis Mall',
  ]

  return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(lines.join('\n'))}`
}

function App() {
  useEffect(() => {
    const items = document.querySelectorAll('.reveal')
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.14 }
    )

    items.forEach((item) => observer.observe(item))
    return () => observer.disconnect()
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const payload = Object.fromEntries(form.entries())
    window.open(buildWhatsAppLink(payload), '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>

      <a
        className="floating-wa"
        href={`https://wa.me/${whatsappNumber}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Book on WhatsApp
      </a>

      <header className="site-header reveal">
        <div className="brand-block">
          <div className="brand-mark" aria-hidden="true">
            {/* <span></span>
            <span></span>
            <span></span> */}
            <img src={Logo} alt="" />
          </div>
          <div>
            {/* <p className="kicker">Bhiwadi Gaming Lounge</p> */}
            <h1>House of Games</h1>
          </div>
        </div>

        <nav className="site-nav" aria-label="Primary navigation">
          <a href="#story">Story</a>
          <a href="#experiences">Experiences</a>
          <a href="#pricing">Pricing</a>
          <a href="#booking">Booking</a>
        </nav>
      </header>

      <main id="main-content">
        <section className="hero reveal">
          <div className="hero-copy">
            <p className="kicker">Bhiwadi Gaming Lounge</p>
            <h2>
              A premium gaming lounge
              <br />
              with a calmer edge.
            </h2>
            <p className="lead">
              House of Games blends PS5, Xbox, wheel simulator, PS5 VR, and snacks into a more refined
              gaming experience built for evenings out, birthday plans, and last-minute bookings.
            </p>

            <div className="hero-actions">
              <a className="btn btn-primary" href="#booking">Book your slot</a>
              <a className="btn btn-secondary" href="#experiences">Explore sessions</a>
            </div>

            <div className="hero-facts">
              <div>
                <span>Open daily</span>
                <strong>11:00 AM — 8:30 PM</strong>
              </div>
              <div>
                <span>Party enquiries</span>
                <strong>No advance payment</strong>
              </div>
            </div>
          </div>

          <div className="hero-media">
            <div className="hero-main-image image-frame">
              <img
                src={images.lounge}
                alt="Premium gaming lounge interior"
                width="1024"
                height="683"
                loading="eager"
              />
            </div>

            <div className="hero-floating-card">
              <p className="kicker">Instant booking</p>
              <strong>Fast enquiries, cleaner experience.</strong>
              <span>Fill the form once and continue directly on WhatsApp.</span>
            </div>
          </div>
        </section>

        <section className="story-grid section reveal" id="story">
          <div className="story-text">
            <p className="kicker">The atmosphere</p>
            <h2>Designed like a place people actually want to spend time in.</h2>
            <p>
              This concept moves away from loud gamer visuals and instead feels more premium, more social,
              and more destination-led. The mood is darker, cleaner, and more visually composed.
            </p>
          </div>

          <div className="stacked-visuals">
            <div className="visual-large image-frame tilt-soft">
              <img
                src={images.accent}
                alt="Green themed PlayStation visual"
                width="1600"
                height="900"
                loading="lazy"
              />
            </div>

            <div className="visual-note">
              <span>Fresh green accents</span>
              <span>Refined dark palette</span>
              <span>Editorial composition</span>
            </div>
          </div>
        </section>

        <section className="section reveal" id="experiences">
          <div className="section-head split">
            <div>
              <p className="kicker">Experiences</p>
              <h2>Choose how you want to play.</h2>
            </div>
            <p>
              Four core formats, clear pricing, and a premium setup for drop-ins, long sessions, and social plans.
            </p>
          </div>

          <div className="experience-list">
            {experiences.map((item, index) => (
              <article className="experience-row" key={item.title}>
                <div className="experience-index">0{index + 1}</div>
                <div className="experience-body">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
                <div className="experience-meta">{item.meta}</div>
              </article>
            ))}
          </div>
        </section>

        <section className="section reveal">
          <div className="editorial-band">
            <div className="band-image image-frame tilt-soft">
              <img
                src={images.simulator}
                alt="Racing simulator setup in a gaming lounge"
                width="640"
                height="1137"
                loading="lazy"
              />
            </div>

            <div className="band-copy">
              <p className="kicker">Groups & parties</p>
              <h2>Birthday plans and group gaming, without the hassle.</h2>
              <p>
                Keep it simple. Send a party enquiry on WhatsApp, skip advance payment at the enquiry stage,
                and coordinate the session directly.
              </p>
              <ul>
                <li>One-click WhatsApp enquiry flow</li>
                <li>Great for friend groups and birthday plans</li>
                <li>Snacks available during sessions</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="section reveal" id="pricing">
          <div className="section-head">
            <div>
              <p className="kicker">Pricing</p>
              <h2>Simple, sharp, and easy to scan.</h2>
            </div>
          </div>

          <div className="pricing-editorial">
            <article className="price-feature">
              <p className="kicker">Console sessions</p>
              <h3>PS5 & Xbox</h3>
              <div className="price-number">₹200</div>
              <p>For one person.</p>
              <p>₹70 extra for each additional player on the same booking.</p>
            </article>

            <article className="price-mini">
              <p className="kicker">Wheel simulator</p>
              <h3>₹200</h3>
              <span>30 minutes per person</span>
            </article>

            <article className="price-mini">
              <p className="kicker">PS5 VR</p>
              <h3>₹200</h3>
              <span>30 minutes per person</span>
            </article>
          </div>
        </section>

        <section className="section reveal" id="booking">
          <div className="booking-wrap">
            <div className="booking-intro">
              <p className="kicker">Book now</p>
              <h2>Send your booking in one smooth step.</h2>
              <p>
                Add your details below and continue instantly on WhatsApp with a prefilled enquiry.
              </p>

              <div className="booking-aside">
                <strong>House of Games</strong>
                <span>Chawri Market, Bhiwadi, near Genesis Mall</span>
                <span>Open daily from 11:00 AM to 8:30 PM</span>

                <a
                    className="map-btn"
                    href="https://www.google.com/maps/search/?api=1&query=House+of+Games+Chawri+Market+Bhiwadi+near+Genesis+Mall"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    View on Google Maps
                </a>
                </div>
            </div>

            <form className="booking-form" onSubmit={handleSubmit}>
              <label>
                Name
                <input name="name" type="text" placeholder="Your full name" required />
              </label>

              <label>
                Phone Number
                <input name="phone" type="tel" placeholder="Your phone number" required />
              </label>

              <label>
                Booking Type
                <select name="bookingType" defaultValue="PS5">
                  <option>PS5</option>
                  <option>Xbox</option>
                  <option>Wheel Simulator</option>
                  <option>PS5 VR</option>
                  <option>Party Booking</option>
                </select>
              </label>

              <label>
                Date
                <input name="date" type="date" required />
              </label>

              <label>
                Time
                <input name="time" type="time" required />
              </label>

              <label>
                Number of People
                <input name="people" type="number" min="1" max="10" placeholder="How many people?" required />
              </label>

              <label className="full">
                Message
                <textarea
                  name="message"
                  rows="4"
                  placeholder="Any party detail, session preference, or request?"
                ></textarea>
              </label>

              <button className="btn btn-primary full" type="submit">
                Continue on WhatsApp
              </button>
            </form>
          </div>
        </section>

        <section className="section reveal">
          <div className="section-head">
            <div>
              <p className="kicker">FAQ</p>
              <h2>Before you visit.</h2>
            </div>
          </div>

          <div className="faq-grid">
            {faqs.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </div>
  )
}

export default App